import { Injectable, Injector } from '@angular/core';
import {
    ApplicationStatus,
    ApplicationType,
    ApplicationUploadFilesInput,
    GeneralFileDto,
    GeneralFileInfoDto,
    PersonApplicationSummaryDto,
    VacPharmApplicationDto,
    VacPharmServiceProxy,
    VacPharmStatus,
    VacPharmPersonDto,
    VacPharmDocumentServiceProxy,
    VacPharmDocumentGroup,
    VacPharmRegisterOutput,
    VacPharmPersonServiceProxy,
    VacPharmContactServiceProxy,
    VacPharmContactDto,
    CreateOrUpdateVacPharmContactInput,
    VacPharmBusinessServiceProxy,
    VacPharmBusinessDto,
    UpdateVacPharmBusinessesOutput,
    UpdateVacPharmBusinessesInput,
} from '@shared/service-proxies/service-proxies';
import { ApplicationService } from '../application/application.service';
import { AppComponentBase } from '@shared/common/app-component-base';
import { map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { CodeService } from '../common/code/code.service';
import { get } from 'http';

@Injectable({
    providedIn: 'root',
})
export class VacPharmService extends AppComponentBase {
    vacPharm: VacPharmApplicationDto = {} as VacPharmApplicationDto;
    vacPharmService: any;
    applicationType: ApplicationType | undefined;

    private vacPharmsForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>(1);
    readonly vacPharmsForPerson$ = this.vacPharmsForPersonSubject.asObservable();

    constructor(
        private readonly applicationService: ApplicationService,
        private readonly vacPharmServiceProxy: VacPharmServiceProxy,
        private readonly vacPharmPersonService: VacPharmPersonServiceProxy,
        private readonly vacPharmContactService: VacPharmContactServiceProxy,
        private readonly vacPharmBusinessService: VacPharmBusinessServiceProxy,
        private readonly vacPharmDocumentService: VacPharmDocumentServiceProxy,
        private readonly codeService: CodeService,
        injector: Injector
    ) {
        super(injector);
    }

    loadVacPharmsForPerson(gln: string): Observable<PersonApplicationSummaryDto[]> {
        if (gln && this.isGranted('Pages.Authority.Applications.Person')) {
            return this.vacPharmServiceProxy.getVacPharmsForPerson(gln).pipe(
                tap(vacPharms => this.vacPharmsForPersonSubject.next(vacPharms))
            );
        } else {
            this.vacPharmsForPersonSubject.next([]);
            return of([]);
        }
    }

    public getVacPharm(caseId: string): Observable<VacPharmApplicationDto> {
        return this.vacPharmServiceProxy.getVacPharmApplication(caseId).pipe(
            tap((output: VacPharmApplicationDto) => {
                this.vacPharm = output;
                this.applicationType = this.vacPharm.application.type;
            })
        );
    }

    public closeVacPharm(): void {
        this.vacPharm = {} as VacPharmApplicationDto;
    }

    get caseId(): string {
        return this.vacPharm?.application.caseId ?? '';
    }

    get status(): ApplicationStatus {
        return this.vacPharm?.application.status ?? ApplicationStatus.UnderConstruction;
    }

    public getMyVacPharms(): Observable<PersonApplicationSummaryDto[]> {
        return this.vacPharmServiceProxy.getMyVacPharms().pipe();
    }

    getStepUrl(nextStep: VacPharmStatus): string {
        return this.applicationService.getNextStep(VacPharmService.getUrlForStep(nextStep));
    }

    static getUrlForStep(step: VacPharmStatus): string {
        switch (step) {
            case VacPharmStatus.Person:
                return 'person';
            case VacPharmStatus.Contact:
                return 'contact';
            case VacPharmStatus.Business:
                return 'business';
            case VacPharmStatus.Release:
            case VacPharmStatus.Released:
                return 'release';
            case VacPharmStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return '';
        }
    }

    getNextStatus(status: VacPharmStatus): Observable<VacPharmStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.vacPharmServiceProxy.getNextStatus(this.caseId, status).pipe(map(status => {
            if (isAuthority && status === VacPharmStatus.Release) {
                return VacPharmStatus.FinishReview;
            }
            return status;
        }));
    }

    setNextStatus(status: VacPharmStatus): Observable<VacPharmStatus> {
        return this.vacPharmServiceProxy.setNextStatus(this.caseId, status).pipe();
    }

    getPerson(): Observable<VacPharmPersonDto> {
        if (this.vacPharm === null) {
            this.getVacPharm(this.caseId).subscribe();
        }
        return of(this.vacPharm.vacPharmPerson).pipe();
    }

    getGlnForLegitimation(): Observable<string> {
        return this.vacPharmPersonService.getGlnForLegitimation(this.caseId).pipe();
    }

    validateGlnForVacPharm(gln: string): Observable<VacPharmRegisterOutput> {
        return this.vacPharmPersonService.validateGlnForVacPharm(gln, this.caseId).pipe();
    }

    acceptRegisterCheck(): Observable<VacPharmStatus> {
        this.applicationService.setStepToReview(VacPharmStatus.Person);
        return this.vacPharmPersonService.acceptRegisterCheck(this.caseId).pipe(tap((status: VacPharmStatus) => {
            this.vacPharm.currentStep = status;
        }));
    }

    getContact(): Observable<VacPharmContactDto> {
        return this.vacPharmContactService.getVacPharmContact(this.caseId).pipe();
    }

    createOrUpdateContact(vacPharmContact: VacPharmContactDto): Observable<VacPharmStatus> {
        let input = {
            caseId: this.caseId,
            vacPharmContact: vacPharmContact
        } as CreateOrUpdateVacPharmContactInput;
        return this.vacPharmContactService.createOrUpdateVacPharmContact(input).pipe();
    }

    getBusinesses(): Observable<VacPharmBusinessDto[]> {
        return this.vacPharmBusinessService.getBusinesses(this.caseId).pipe();
    }

    createPlaceholderBusiness(): Observable<number> {
        return this.vacPharmBusinessService.createPlaceholderBusiness(this.caseId).pipe();
    }

    updateBusinesses(input: UpdateVacPharmBusinessesInput): Observable<null> {
        this.applicationService.setStepToReview(VacPharmStatus.Business);
        return this.vacPharmBusinessService.updateBusinesses(input).pipe(tap((output: UpdateVacPharmBusinessesOutput) => {
            this.vacPharm.vacPharmBusinesses = output.businesses;
            this.vacPharm.currentStep = output.status;
        })).pipe(map(() => null));
    }

    getDocuments(status: VacPharmStatus): Observable<VacPharmDocumentGroup[]> {
        return this.vacPharmDocumentService.getDocumentsForVacPharm(this.caseId, status).pipe();
    }

    getFile(fileId: number, step: VacPharmStatus): Observable<GeneralFileDto> {
        return this.vacPharmDocumentService.getFile(this.caseId, step, fileId);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.vacPharmDocumentService.uploadFiles(input);
    }

    deleteFile(id: number, step: VacPharmStatus): Observable<void> {
        return this.vacPharmDocumentService.deleteFile(this.caseId, step, id);
    }

    checkDocuments(): Observable<VacPharmStatus[]> {
        return this.vacPharmServiceProxy.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }

    releaseVacPharm(comment: string): Observable<void> {
        return this.vacPharmServiceProxy.releaseVacPharm(this.caseId, comment);
    }

    // getEvaluationComments(): Observable<string> {
    //     return this.vacPharmServiceProxy.getEvaluationComments(this.caseId);
    // }

    getPersonId(): Observable<number> {
        return this.vacPharmServiceProxy.getPersonId(this.caseId).pipe();
    }
}
